import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { useDepot } from '#~/depot/hooks.js';
import { BootErrorScreen } from './boot-error-screen.js';
import {
  enterBootHumanWait,
  getBootSnapshot,
  recoverBoot,
  subscribeRendererHealth,
  subscribeBootBudget
} from '../root/boot-budget.js';

/** Only a committed, uncovered action surface excludes operator time. */
export function useRenderedBootHumanWait(waiting: boolean): void {
  const visible = useDepot(
    (state) =>
      state.boot.isRootReady &&
      state.synchronizer.hasRegistryLoaded &&
      !state.boot.error &&
      !['checking', 'linking', 'verifying_link', 'error'].includes(state.boot.autoLinkState.status)
  );
  const state = useSyncExternalStore(subscribeBootBudget, getBootSnapshot);
  useEffect(() => {
    if (waiting && visible) return enterBootHumanWait();
    return undefined;
  }, [waiting, visible, state?.attemptId]);
}

/** Above auth/Root so a pre-Root timeout can show the existing actionable error. */
export function BootBudgetGate({ children }: { children: ReactNode }): ReactNode {
  useEffect(subscribeRendererHealth, []);
  const state = useSyncExternalStore(subscribeBootBudget, getBootSnapshot);
  const error = useDepot((value) => value.boot.error);
  // This gate owns ONE failure: a launch that never finished -- the watchdog's
  // `boot_timeout`, which is the whole point of a bounded startup. It must not
  // own the others, because taking the screen here is terminal: the snapshot
  // stays `failed` for the rest of the launch, so whatever recovers underneath
  // can never get the display back, and only a reload clears it.
  //
  // Every other failure already has an owner that renders the right thing and
  // stays recoverable: a thrown error renders the error boundary's <Fallback>
  // with the classified copy for THAT error, the overlay's stall watchdog
  // publishes `boot.error` and renders it inside the overlay, and Root's
  // bootstrap renders its own account-bootstrap error state. Before this PR
  // those errors were page-level and survivable -- on `main` the boundary showed
  // the real message plus a "Go to home page" escape and the launch kept living.
  // Escalating them into a terminal launch failure took that away, and the
  // surfaces it takes away are the pre-Studio ones a real user sits in: Bridge
  // Setup and studio creation are `pre-ready` by definition, so a transient 5xx
  // or a dropped connection there became a dead end.
  //
  // The bound itself is untouched: the deadline still fires, still settles the
  // attempt, and still shows this screen. Per rule: reference-rules-in-comments
  // (ai.knowledge/global).
  if (state?.outcome !== 'failed' || state.reason !== 'boot_timeout') return children;
  return (
    <BootErrorScreen
      source='budget-gate'
      error={
        error ?? {
          message: 'Studio could not finish loading.',
          detail: 'Please try again.',
          severity: 'retryable',
          retryFn: () => {
            void recoverBoot('retry');
          }
        }
      }
    />
  );
}
